import React from 'react';
import {
  Box,
  Select,
  MenuItem,
  useTheme,
  useMediaQuery,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Tooltip,
  alpha
} from '@mui/material';
import {
  Business as BusinessIcon,
  Science as ScienceIcon,
  Receipt as ReceiptIcon,
  VerifiedUser as VerifiedUserIcon,
  IntegrationInstructions as IntegrationInstructionsIcon,
  Build as BuildIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAppMode } from '../../contexts/AppModeContext';
import { useImpersonatedAuth } from '../../hooks/useImpersonatedAuth';
import { useComponentAccess } from '../../hooks/useComponentAccess';

type SwitcherMode = 'crm' | 'lis' | 'billing' | 'vob' | 'integrations' | 'tools';

const ModeSwitcher: React.FC = () => {
  const { mode, setMode } = useAppMode();
  const navigate = useNavigate();
  const { user } = useImpersonatedAuth();
  const { hasAccess, isLoading: accessLoading } = useComponentAccess();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const isSystemAdmin = user?.is_system_admin;
  // A portal/section is visible when the user holds a grant on ANY component
  // that lives inside it (system admins see all). Items inside are still
  // filtered by their own per-component gate. Component sets mirror
  // ComponentAccessService's Billing / Laboratory groups.
  const BILLING_COMPONENTS = ['billing', 'billing-daily-monitoring', 'billing-reports', 'revenue-analytics', 'insurance-claims', 'billing-settings'];
  const LIS_COMPONENTS = ['lis', 'lis-orders', 'medical-tests'];
  const canSeeBilling = isSystemAdmin || BILLING_COMPONENTS.some((c) => hasAccess(c));
  const canSeeLis = isSystemAdmin || LIS_COMPONENTS.some((c) => hasAccess(c));
  // The Integrations portal is visible to system admins or anyone holding a grant on one
  // of the integration surfaces it hosts. Items inside are still filtered by their own gate.
  const canSeeIntegrations =
    isSystemAdmin ||
    hasAccess('google-workspace') ||
    hasAccess('integrations-file-monitor');
  const allowedModes = React.useMemo<Array<SwitcherMode>>(
    () => {
      const modes: SwitcherMode[] = ['crm'];
      if (canSeeLis) modes.push('lis');
      if (canSeeBilling) modes.push('billing');
      // VOB remains system-admin only (no per-component gate of its own yet).
      if (isSystemAdmin) modes.push('vob');
      if (canSeeIntegrations) modes.push('integrations');
      // Tools portal is system-admin only (it hosts infrastructure-level tools
      // like the S3 Browser that cross tenant boundaries).
      if (isSystemAdmin) modes.push('tools');
      return modes;
    },
    [isSystemAdmin, canSeeLis, canSeeBilling, canSeeIntegrations]
  );

  // Force CRM if current mode is not permitted. Wait until the component-access
  // query has resolved — while it's in-flight hasAccess() returns false, so a
  // non-admin grant-holder's allowedModes would momentarily exclude
  // 'integrations' and this effect would wipe their persisted mode on every
  // reload (it never restores once the grant loads in). Skipping while loading
  // keeps the persisted mode intact until we actually know their grants.
  React.useEffect(() => {
    if (accessLoading) return;
    if (!allowedModes.includes(mode)) {
      setMode('crm');
    }
  }, [accessLoading, allowedModes, mode, setMode]);

  const navigateForMode = (newMode: SwitcherMode) => {
    if (newMode === 'lis') {
      navigate('/lis');
    } else if (newMode === 'billing') {
      // Land on Daily Monitoring — the `/billing` orders-list page is hidden from nav
      // (that workflow is unused; Daily Monitoring replaces it).
      navigate('/billing/monitoring');
    } else if (newMode === 'vob') {
      navigate('/vob');
    } else if (newMode === 'integrations') {
      navigate('/integrations');
    } else if (newMode === 'tools') {
      navigate('/tools');
    } else {
      navigate('/my-assignments');
    }
  };

  const handleModeChange = (event: React.MouseEvent<HTMLElement>, newMode: SwitcherMode | null) => {
    if (newMode !== null && newMode !== mode) {
      setMode(newMode);
      navigateForMode(newMode);
    }
  };

  const handleSelectChange = (event: any) => {
    const newMode = event.target.value as SwitcherMode;
    if (newMode && newMode !== mode) {
      setMode(newMode);
      navigateForMode(newMode);
    }
  };

  return (
    <Box 
      sx={{ 
        display: 'flex', 
        alignItems: 'center'
      }}
    >
      {isMobile ? (
        <Select
          variant="outlined"
          size="small"
          value={allowedModes.includes(mode) ? mode : 'crm'}
          onChange={handleSelectChange}
          sx={{
            minWidth: 100,
            borderRadius: 2,
            backgroundColor: alpha('#ff8c00', 0.1),
            color: '#ff8c00',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: '#ff8c00'
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#ff8c00'
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#ff8c00'
            },
            '&:hover': {
              backgroundColor: alpha('#ff8c00', 0.1)
            },
            '&.Mui-focused': {
              boxShadow: `0 0 0 2px ${alpha('#ff8c00', 0.1)}`
            },
            '& .MuiSvgIcon-root': {
              color: '#ff8c00'
            },
            '& .MuiSelect-select': {
              py: 0.75,
              px: 1.5,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              color: '#ff8c00'
            }
          }}
        >
          {allowedModes.map((m) => (
            <MenuItem
              key={m}
              value={m}
              sx={{
                color: '#ff8c00',
                '&:hover': {
                  backgroundColor: `${alpha('#ff8c00', 0.1)} !important`
                },
                '&.Mui-selected': {
                  backgroundColor: '#ff8c00 !important',
                  color: '#000000',
                  '& .MuiSvgIcon-root': { color: '#000000' }
                }
              }}
            >
              <Box display="flex" alignItems="center" gap={1}>
                {m === 'crm' && <BusinessIcon fontSize="small" />}
                {m === 'lis' && <ScienceIcon fontSize="small" />}
                {m === 'billing' && <ReceiptIcon fontSize="small" />}
                {m === 'vob' && <VerifiedUserIcon fontSize="small" />}
                {m === 'integrations' && <IntegrationInstructionsIcon fontSize="small" />}
                {m === 'tools' && <BuildIcon fontSize="small" />}
                <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1 }}>
                  {m === 'crm' ? 'CRM' : m === 'lis' ? 'LIS' : m === 'billing' ? 'Billing' : m === 'vob' ? 'VOB' : m === 'integrations' ? 'Integrations' : 'Tools'}
                </Typography>
              </Box>
            </MenuItem>
          ))}
        </Select>
      ) : (
        <ToggleButtonGroup
          value={allowedModes.includes(mode) ? mode : 'crm'}
          exclusive
          onChange={handleModeChange}
          size="small"
          sx={{
            borderRadius: 2,
            backgroundColor: alpha('#ff8c00', 0.1),
            border: `1px solid #ff8c00`,
            '& .MuiToggleButton-root': {
              px: 2,
              py: 0.75,
              textTransform: 'none',
              fontWeight: 500,
              border: 'none',
              color: '#ff8c00',
              '&.Mui-selected': {
                backgroundColor: '#ff8c00',
                color: '#000000',
                '&:hover': {
                  backgroundColor: '#ff8c00',
                  color: '#000000',
                }
              },
              '&:not(.Mui-selected)': {
                backgroundColor: 'transparent',
                color: '#ff8c00',
                '&:hover': {
                  backgroundColor: alpha('#ff8c00', 0.1),
                  color: '#ff8c00',
                }
              }
            }
          }}
        >
          {allowedModes.includes('crm') && (
            <ToggleButton value="crm">
              <Tooltip title="Customer Relationship Management">
                <Box display="flex" alignItems="center" gap={1}>
                  <BusinessIcon fontSize="small" />
                  <Typography variant="body2">CRM</Typography>
                </Box>
              </Tooltip>
            </ToggleButton>
          )}
          {allowedModes.includes('lis') && (
            <ToggleButton value="lis">
              <Tooltip title="Laboratory Information System">
                <Box display="flex" alignItems="center" gap={1}>
                  <ScienceIcon fontSize="small" />
                  <Typography variant="body2">LIS</Typography>
                </Box>
              </Tooltip>
            </ToggleButton>
          )}
          {allowedModes.includes('billing') && (
            <ToggleButton value="billing">
              <Tooltip title="Billing & Finance Portal">
                <Box display="flex" alignItems="center" gap={1}>
                  <ReceiptIcon fontSize="small" />
                  <Typography variant="body2">Billing</Typography>
                </Box>
              </Tooltip>
            </ToggleButton>
          )}
          {allowedModes.includes('vob') && (
            <ToggleButton value="vob">
              <Tooltip title="Verification of Benefits">
                <Box display="flex" alignItems="center" gap={1}>
                  <VerifiedUserIcon fontSize="small" />
                  <Typography variant="body2">VOB</Typography>
                </Box>
              </Tooltip>
            </ToggleButton>
          )}
          {allowedModes.includes('integrations') && (
            <ToggleButton value="integrations">
              <Tooltip title="Integration Management">
                <Box display="flex" alignItems="center" gap={1}>
                  <IntegrationInstructionsIcon fontSize="small" />
                  <Typography variant="body2">Integrations</Typography>
                </Box>
              </Tooltip>
            </ToggleButton>
          )}
          {allowedModes.includes('tools') && (
            <ToggleButton value="tools">
              <Tooltip title="Admin Tools">
                <Box display="flex" alignItems="center" gap={1}>
                  <BuildIcon fontSize="small" />
                  <Typography variant="body2">Tools</Typography>
                </Box>
              </Tooltip>
            </ToggleButton>
          )}
        </ToggleButtonGroup>
      )}
    </Box>
  );
};

export default ModeSwitcher;

